import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/apiHelper';
import Page from '../molecules/Page';
import posthog from 'posthog-js';
import { useCookieConsent } from '../../contexts/CookieConsentContext';

const CompleteProfile = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consentState, handleSaveConsent } = useCookieConsent();

    const suggestedNickname = location.state?.email ? location.state.email.split('@')[0] : '';

    const [nickname, setNickname] = useState(suggestedNickname);
    const [marketingSource, setMarketingSource] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [marketingConsent, setMarketingConsent] = useState(false);
    const [analyticsConsent, setAnalyticsConsent] = useState(false);
    const allConsentsAccepted = termsAccepted && marketingConsent && analyticsConsent;
    const handleSelectAllConsents = (checked) => {
        setTermsAccepted(checked);
        setMarketingConsent(checked);
        setAnalyticsConsent(checked);
    };
    const [fieldErrors, setFieldErrors] = useState({});

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFieldErrors({}); 

        let errors = {};
        if (!nickname.trim()) {
            errors.nickname = 'Nickname jest wymagany.';
        }
        if (!marketingSource) {
            errors.marketing_source = 'To pole jest wymagane.';
        }
        if (!termsAccepted) {
            errors.terms = 'Akceptacja regulaminu i polityki prywatności jest obowiązkowa.';
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        posthog.capture('complete_profile_submitted', { method: 'social' });

        try {
            const response = await api.patch('/users/me/complete-profile/', {
                nickname,
                marketing_source: marketingSource,
                consents: [
                    { consent_type: 'TERMS_OF_SERVICE', status: 'GRANTED', version: '2.0' },
                    { consent_type: 'PRIVACY_POLICY', status: 'GRANTED', version: '1.0' },
                    { consent_type: 'EMAIL_MARKETING', status: marketingConsent ? 'GRANTED' : 'REVOKED', version: '1.0' },
                    { consent_type: 'ANALYTICS', status: analyticsConsent ? 'GRANTED' : 'REVOKED', version: '1.0' }
                ]
            });

            localStorage.setItem("user_preferences", JSON.stringify(response.data.preferences));
            localStorage.setItem('showOnboarding', 'true');
            posthog.capture('complete_profile_succeeded', { method: 'social' });
            // Spinamy wybór analityki z realnym stanem PostHoga (opt-in/opt-out + localStorage).
            handleSaveConsent({ ...consentState, analytics: analyticsConsent });
            navigate('/app');

        } catch (err) {
            posthog.capture('complete_profile_failed', { method: 'social', status: err.response?.status || null });
            const errorDetail = err.response?.data?.detail || 'Wystąpił błąd. Spróbuj ponownie.';
            const newFieldErrors = {};
            if (err.response?.data?.nickname) {
                 newFieldErrors.nickname = err.response.data.nickname.join(" ");
            } else {
                newFieldErrors.general = errorDetail;
            }
            setFieldErrors(newFieldErrors);
        }
    };

    return (
        <Page showNav={false}>
            <div className="flex flex-col max-w-2xl mx-auto w-full flex-grow px-4 py-8 lg:px-0">
                <div className="bg-white rounded-xl lg:rounded-[3rem] shadow-[4px_4px_12px_rgba(0,0,0,0.35)] border border-gray-200 py-8 px-6 lg:px-14 w-full">
                    <h2 className="text-center text-xl font-apotek uppercase tracking-widest font-semibold text-[#233855] mb-6">
                        Uzupełnij swój profil
                    </h2>
                    <p className="text-center text-sm text-gray-600 mb-8">
                        Już prawie koniec! Potrzebujemy jeszcze kilku informacji.
                    </p>
                    <form className="space-y-5" onSubmit={handleSubmit}>
                        <div>
                            <label htmlFor="nickname" className="block text-[0.8rem] font-apotek uppercase tracking-widest font-semibold text-[#233855] mb-2">
                                Jak masz na imię? <span className="text-red-600 ml-1">*</span>
                            </label>
                            <input type="text" name="nickname" id="nickname" required value={nickname} onChange={(e) => setNickname(e.target.value)} className="ph-no-capture block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#233855] focus:ring-1 focus:ring-[#233855] sm:text-sm/6" />
                            {fieldErrors.nickname && <p className="text-xs text-red-600 mt-1">{fieldErrors.nickname}</p>}
                        </div>

                        <div>
                            <label htmlFor="marketing-source" className="block text-[0.8rem] font-apotek uppercase tracking-widest font-semibold text-[#233855] mb-2">
                                Jak się o nas dowiedziałeś/aś? <span className="text-red-600 ml-1">*</span>
                            </label>
                            <select id="marketing-source" name="marketing-source" value={marketingSource} onChange={(e) => setMarketingSource(e.target.value)} required className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#233855] focus:ring-1 focus:ring-[#233855] sm:text-sm/6">
                                <option value="" disabled>Wybierz opcję...</option>
                                <option value="Facebook">Facebook</option>
                                <option value="Instagram">Instagram</option>
                                <option value="TikTok">TikTok</option>
                                <option value="Youtube">YouTube</option>
                                <option value="Wyszukiwarka">Wyszukiwarka</option>
                                <option value="Od znajomego">Od znajomego</option>
                                <option value="Inne">Inne</option>
                            </select>
                            {fieldErrors.marketing_source && <p className="text-xs text-red-600 mt-1">{fieldErrors.marketing_source}</p>}
                        </div>
                        
                        {/* --- SEKCJA ZGÓD (ZMODYFIKOWANA - ZGODNA Z REGISTER) --- */}
                        <div className="pt-2 space-y-4">

                            {/* Zaznacz wszystko */}
                            <div className="relative flex items-center">
                                <div className="flex h-5 items-center">
                                    <input id="select-all-consents" name="select-all-consents" type="checkbox" checked={allConsentsAccepted} onChange={(e) => handleSelectAllConsents(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#233855] focus:ring-[#345081] cursor-pointer" />
                                </div>
                                <label htmlFor="select-all-consents" className="ml-3 text-sm font-bold text-[#233855] cursor-pointer select-none">
                                    Zaznacz wszystkie zgody
                                </label>
                            </div>

                            {/* Checkbox 1: Regulamin + Polityka */}
                            <div className="relative flex items-start">
                                <div className="flex h-5 items-center mt-0.5">
                                    <input id="terms" name="terms" type="checkbox" required checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#233855] focus:ring-[#345081] cursor-pointer" />
                                </div>
                                <div className="ml-3 text-sm text-gray-700 text-justify leading-snug">
                                    <label htmlFor="terms" className="cursor-pointer">
                                        Oświadczam, że zapoznałem się z{' '}
                                        <a href="https://matematly.pl/terms-of-service" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#8A0000] hover:text-[#345081]">regulaminem aplikacji</a>
                                        {' '}oraz{' '}
                                        <a href="https://matematly.pl/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#8A0000] hover:text-[#345081]">polityką prywatności</a>
                                        {' '}oraz akceptuję regulamin
                                        <span className="text-red-600 ml-1">*</span>
                                    </label>
                                    {fieldErrors.terms && <p className="text-xs text-red-600 mt-1">{fieldErrors.terms}</p>}
                                </div>
                            </div>

                            {/* Checkbox 2: Marketing */}
                            <div className="relative flex items-start">
                                <div className="flex h-5 items-center mt-0.5">
                                    <input id="marketing" name="marketing" type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#233855] focus:ring-[#345081] cursor-pointer" />
                                </div>
                                <div className="ml-3 text-[0.7rem] leading-relaxed text-gray-600 text-justify">
                                    <label htmlFor="marketing" className="cursor-pointer">
                                        Wyrażam zgodę na używanie mojego adresu e-mail przez <strong>MATEMATLY sp. z o.o.</strong> do celów przesyłania informacji handlowej w rozumieniu przepisów ustawy z dnia 18 lipca 2002 r. o świadczeniu usług drogą elektroniczną, w tym marketingu bezpośredniego, za pośrednictwem poczty elektronicznej.
                                    </label>
                                </div>
                            </div>

                            {/* Checkbox 3: Analityka */}
                            <div className="relative flex items-start">
                                <div className="flex h-5 items-center mt-0.5">
                                    <input id="analytics" name="analytics" type="checkbox" checked={analyticsConsent} onChange={(e) => setAnalyticsConsent(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#233855] focus:ring-[#345081] cursor-pointer" />
                                </div>
                                <div className="ml-3 text-[0.7rem] leading-relaxed text-gray-600 text-justify">
                                    <label htmlFor="analytics" className="cursor-pointer">
                                        Wyrażam zgodę na przetwarzanie przez <strong>MATEMATLY sp. z o.o.</strong> danych dotyczących mojego sposobu korzystania z aplikacji w celach analitycznych, w szczególności w celu ulepszania i rozwoju produktu. Zgodę mogę w każdej chwili wycofać w ustawieniach, co nie wpływa na zgodność z prawem przetwarzania dokonanego przed jej wycofaniem.
                                    </label>
                                </div>
                            </div>

                            {/* Klauzula RODO */}
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                                <p className="text-[0.65rem] text-gray-500 leading-tight text-justify">
                                    Administratorem danych osobowych zbieranych za pośrednictwem aplikacji jest <strong>MATEMATLY sp. z o.o. w Krakowie</strong>. Dane są lub mogą być przetwarzane w celach oraz na podstawach wskazanych szczegółowo w polityce prywatności (np. dokonywanie zakupów, marketing bezpośredni). Polityka prywatności zawiera pełną informację na temat przetwarzania danych przez administratora wraz z prawami przysługującymi osobie, której dane dotyczą. Szybki kontakt z administratorem: <a href="mailto:kontakt@matematly.pl" className="text-gray-700 hover:underline">kontakt@matematly.pl</a>.
                                </p>
                            </div>

                        </div>
                        {/* --- KONIEC SEKCJI ZGÓD --- */}
                        
                        {fieldErrors.general && <p className="text-xs text-center text-red-600 pt-2">{fieldErrors.general}</p>}
                        
                        <div className="pt-4">
                            <button type="submit" className="flex w-full justify-center rounded-lg bg-[#233855] px-3 py-2 text-sm font-apotek uppercase tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#233855]">
                                ZACZYNAJMY!
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Page>
    );
};

export default CompleteProfile;